// Creators 인라인 SVG 차트 — 스케일/축 공용 헬퍼.
// 차트 라이브러리 미도입(레포 정책) — RevenueTrendChart 인라인 SVG 로직을 추출해
// Overview(매출)·Performance(브랜디드 추이·매출·Hub)에서 공용으로 재사용한다.
import { formatMonthShort } from './overview/format'
export interface ChartGeom {
  w: number
  h: number
  padX: number
  top: number
  bottom: number
}

/** 막대/밴드 중심 x — 밴드 폭 = (w-2·padX)/n, 각 밴드 중앙. (막대·밴드정렬 라인용) */
export function xBand(i: number, n: number, g: ChartGeom): number {
  const step = (g.w - g.padX * 2) / Math.max(1, n)
  return g.padX + step * i + step / 2
}

/** 라인 포인트 x — 양 끝점이 좌우 가장자리(padX ~ w-padX)에 붙음. (연속 라인용) */
export function xPoint(i: number, n: number, g: ChartGeom): number {
  if (n <= 1) return g.w / 2
  return g.padX + (i * (g.w - g.padX * 2)) / (n - 1)
}

/** 값 → y좌표(top=위, bottom=아래). max<=0 가드. */
export function yAt(value: number, max: number, g: ChartGeom): number {
  const m = max > 0 ? max : 0.0001
  return g.bottom - (value / m) * (g.bottom - g.top)
}

const finite = (v: number | null): v is number => v != null && Number.isFinite(v)

/** 라인 — null 갭을 건너뛴 연속 구간별 polyline points 문자열 배열(0 강제 금지).
 *  xFn = xPoint(연속 라인) 기본 / xBand(막대 정렬 라인) 선택. */
export function lineSegments(
  values: (number | null)[],
  max: number,
  g: ChartGeom,
  xFn: (i: number, n: number, g: ChartGeom) => number = xPoint,
): string[] {
  const n = values.length
  const segs: string[] = []
  let cur: string[] = []
  values.forEach((v, i) => {
    if (!finite(v)) {
      if (cur.length) segs.push(cur.join(' '))
      cur = []
      return
    }
    cur.push(`${xFn(i, n, g).toFixed(1)},${yAt(v, max, g).toFixed(1)}`)
  })
  if (cur.length) segs.push(cur.join(' '))
  return segs
}

/** 마지막 non-null 인덱스(없으면 -1) — 라인 끝점 마커용. */
export function lastDefined(values: (number | null)[]): number {
  for (let i = values.length - 1; i >= 0; i--) if (finite(values[i])) return i
  return -1
}

/** non-null·유한 최대값(없으면 0). */
export function maxDefined(values: (number | null)[]): number {
  return values.reduce<number>((m, v) => (finite(v) && v > m ? v : m), 0)
}

export interface AxisLabel {
  /** 원본 배열에서의 위치 — x 좌표는 호출부가 xPoint(i, n, geom)로 계산한다. */
  i: number
  text: string
}

/** x축 월 라벨 솎아내기 — 집계창이 전체 기간으로 바뀌며 월 수가 수십 개가 됐다.
 *  **데이터는 전량 그리고 라벨만 줄인다**(값을 감추지 않는다).
 *    · 12개월 이하 → 기존처럼 매월("9월")
 *    · 그 이상 → 연 경계(01월)를 라벨로. 연 라벨이 2~maxLabels개면 연도만("2024") 표기
 *    · 연 라벨이 부족·과다하면 균등 간격으로 "YY.MM"
 *  마지막 달은 "지금"의 기준점이라 항상 남긴다(바로 앞과 붙지 않는 한). */
export function pickAxisLabels(months: string[], maxLabels = 12): AxisLabel[] {
  const n = months.length
  if (n === 0) return []
  if (n <= maxLabels) return months.map((m, i) => ({ i, text: formatMonthShort(m) }))

  const yearStarts: number[] = []
  months.forEach((m, i) => {
    if (m.slice(5, 7) === '01') yearStarts.push(i)
  })
  const useYears = yearStarts.length >= 2 && yearStarts.length <= maxLabels
  const idx = useYears ? [...yearStarts] : evenIndices(n, maxLabels)
  const last = idx[idx.length - 1]
  if (last == null || last < n - 2) idx.push(n - 1)

  return idx.map((i) => {
    const m = months[i]
    const asYear = useYears && m.slice(5, 7) === '01'
    return { i, text: asYear ? m.slice(0, 4) : `${m.slice(2, 4)}.${m.slice(5, 7)}` }
  })
}

/** 0..n-1을 최대 count개로 균등 샘플링(항상 0 포함).
 *  step은 **올림**이어야 상한이 보장된다 — 반올림이면 n=14·count=12에서 step 1이 되어
 *  14개가 그대로 나온다(검증에서 잡힌 실제 케이스). */
function evenIndices(n: number, count: number): number[] {
  const step = Math.max(1, Math.ceil(n / Math.max(1, count)))
  const out: number[] = []
  for (let i = 0; i < n; i += step) out.push(i)
  return out
}
